import { browserName, osName } from 'react-device-detect';

export const FLAGLE_URL = 'https://www.flagle.io';

/**
 * Short form for links people paste into chats. Cloudflare redirects the
 * apex to www, so dropping the subdomain costs nothing and saves four
 * characters in a message that is mostly URL.
 */
export const FLAGLE_SHORT_URL = 'https://flagle.io';

/**
 * Firefox on Android advertises `navigator.share` but drops the `text`
 * payload, which is the entire message here — so it stays on the clipboard
 * path. Everything else is a straight feature detection.
 */
export const canUseNativeShare = () =>
  typeof navigator !== 'undefined' &&
  typeof navigator.share === 'function' &&
  !(browserName === 'Firefox' && osName === 'Android');

/**
 * Where a share ended up. Doubles as the GA `method` param and the
 * `utm_medium` on the shared link, so the outbound click and the inbound
 * visit can be joined up in reporting.
 */
export type ShareChannel =
  | 'native'
  | 'clipboard'
  | 'link'
  | 'whatsapp'
  | 'telegram'
  | 'x';

/**
 * The link a player sends to friends. Defaults to today's game rather than
 * `/archive/<date>`, because the archive is gated behind Premium and a
 * challenge that opens on a paywall is not a challenge. The replayable modes
 * pass their own path, since those are free and open to everyone.
 */
export const buildChallengeUrl = (channel: ShareChannel, path = '/') =>
  `${FLAGLE_URL}${path}?utm_source=share&utm_medium=${channel}`;

/**
 * Clipboard write with a fallback for browsers that expose no async clipboard
 * (or refuse it outside a secure context). Resolves false when both fail so
 * the caller can tell the player instead of silently doing nothing.
 */
export const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the legacy path below.
  }

  try {
    const textArea = document.createElement('textarea');
    textArea.value = text;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.top = '0';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textArea);
    return copied;
  } catch {
    return false;
  }
};
